import React, { useCallback, useMemo, useState } from 'react';
import {
  Card,
  Form,
  Button,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Row,
  Col,
  Typography,
  App,
  Spin,
  Alert,
  Space,
  Radio,
  Tooltip,
} from 'antd';
import type { FormListFieldData } from 'antd/es/form';
import { ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs, { type Dayjs } from 'dayjs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useWarehouses, useItems } from '@/hooks/useInventory';
import { useActiveCustomers } from '@/hooks/useCustomers';
import { useProjectsForSelection } from '@/hooks/useProject';
import { bomService, type BomMasterDto } from '@/services/bom.service';
import type { Item } from '@/services/inventory.service';
import { ItemSelect } from '@/components/inventory/ItemSelect';
import quotationService from '@/services/quotation.service';
import * as prApi from '@/services/productionRequest.service';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';

const { Title, Text } = Typography;

function nilUuid(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v).trim();
  return s === '' ? null : s;
}

function formatPrCreateError(e: unknown): string {
  const ax = e as { response?: { data?: unknown }; message?: string };
  const raw = ax.response?.data;
  let msg = ax.message || (e as Error)?.message || 'Save failed';
  let issues: Array<{ message?: string; path?: unknown }> | undefined;

  if (raw && typeof raw === 'object' && raw !== null) {
    const d = raw as { message?: string; error?: string; errors?: typeof issues };
    msg = d.message || d.error || msg;
    issues = Array.isArray(d.errors) ? d.errors : undefined;
  } else if (typeof raw === 'string' && raw.trim()) {
    msg = raw;
  }

  if (Array.isArray(issues) && issues.length) {
    const parts = issues.map((i) => {
      const path = i.path;
      const pathStr = Array.isArray(path) && path.length ? `${path.join('.')}: ` : '';
      const sub = typeof i.message === 'string' ? i.message : JSON.stringify(i);
      return `${pathStr}${sub}`;
    });
    msg = `${msg} — ${parts.join('; ')}`;
  }
  return msg;
}

function PrLineFields({
  field,
  lineIndex,
  remove,
  items,
  itemsLoading,
  boms,
  projectOptions,
}: {
  field: FormListFieldData;
  lineIndex: number;
  remove: (index: number | number[]) => void;
  items: Item[];
  itemsLoading: boolean;
  boms: BomMasterDto[];
  projectOptions: { value: string; label: string }[];
}) {
  const form = Form.useFormInstance();
  const lineSource = Form.useWatch(['lines', field.name, 'line_product_source'], form) ?? 'inventory';
  const itemId = Form.useWatch(['lines', field.name, 'item_id'], form);

  const bomOptions = useMemo(() => {
    if (!itemId) return [];
    return boms
      .filter((b) => b.item_id === itemId && b.is_active !== false)
      .map((b) => ({
        value: b.id,
        label: `${b.bom_code} — ${b.bom_name}${b.is_default ? ' (default)' : ''}`,
      }));
  }, [boms, itemId]);

  const lineName = field.name;
  const isManual = lineSource === 'manual';
  const quoteQty = Form.useWatch(['lines', field.name, 'quote_quantity'], { form, preserve: true }) as
    | number
    | undefined;
  const alreadyRequested = Form.useWatch(['lines', field.name, 'already_requested'], { form, preserve: true }) as
    | number
    | undefined;
  const requestedIn = Form.useWatch(['lines', field.name, 'requested_in'], { form, preserve: true }) as
    | string[]
    | undefined;

  return (
    <Card
      size="small"
      title={`Line ${lineIndex + 1}`}
      style={{ marginBottom: 12 }}
      extra={
        <Button type="link" danger onClick={() => remove(field.name)}>
          Remove line
        </Button>
      }
    >
      <Form.Item name={[lineName, 'boq_line_item_id']} hidden>
        <Input />
      </Form.Item>
      <Form.Item name={[lineName, 'quotation_line_number']} hidden>
        <Input />
      </Form.Item>
      {alreadyRequested != null && alreadyRequested > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 8 }}
          message={`Quotation qty ${quoteQty ?? '-'}; ${alreadyRequested} already requested${
            requestedIn?.length ? ` in ${requestedIn.join(', ')}` : ''
          }. Only the remaining quantity can be requested here.`}
        />
      ) : null}
      <Row gutter={[16, 8]}>
        <Col xs={24}>
          <Form.Item name={[lineName, 'line_product_source']} initialValue="inventory" label="Product" required>
            <Radio.Group
              onChange={(e) => {
                const v = e.target.value as 'inventory' | 'manual';
                if (v === 'manual') {
                  form.setFieldValue(['lines', lineName, 'item_id'], undefined);
                  form.setFieldValue(['lines', lineName, 'bom_id'], undefined);
                } else {
                  form.setFieldValue(['lines', lineName, 'manual_product_code'], undefined);
                  form.setFieldValue(['lines', lineName, 'manual_product_description'], undefined);
                }
              }}
              options={[
                { label: 'From inventory', value: 'inventory' },
                { label: 'Manual entry', value: 'manual' },
              ]}
            />
          </Form.Item>
        </Col>
      </Row>
      {!isManual ? (
        <Row gutter={[16, 8]}>
          <Col xs={24} md={10}>
            <Form.Item
              label="Finished good (item)"
              name={[lineName, 'item_id']}
              rules={[{ required: true, message: 'Select an item' }]}
            >
              <ItemSelect
                items={items}
                autoLoad={false}
                loading={itemsLoading}
                placeholder="Search item"
                onChange={() => {
                  form.setFieldValue(['lines', lineName, 'bom_id'], undefined);
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={8} md={4}>
            <Form.Item
              label="Qty"
              name={[lineName, 'quantity']}
              rules={[{ required: true, message: 'Required' }]}
            >
              <InputNumber min={0.0001} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={8} md={4}>
            <Form.Item
              label="UOM"
              name={[lineName, 'uom']}
              rules={[{ required: true, message: 'Required' }]}
            >
              <Input placeholder="pcs" />
            </Form.Item>
          </Col>
          <Col xs={8} md={6}>
            <Form.Item label="Stock type" name={[lineName, 'stock_type']} initialValue="MTS">
              <Select
                options={[
                  { value: 'MTS', label: 'MTS (make-to-stock)' },
                  { value: 'MTO_hybrid', label: 'MTO / hybrid' },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
      ) : (
        <>
          <Row gutter={[16, 8]}>
            <Col xs={24} md={8}>
              <Form.Item label="Product code" name={[lineName, 'manual_product_code']}>
                <Input placeholder="e.g. drawing / customer part #" maxLength={100} />
              </Form.Item>
            </Col>
            <Col xs={24} md={16}>
              <Form.Item
                label="Description"
                name={[lineName, 'manual_product_description']}
                rules={[
                  {
                    validator: async () => {
                      const code = form.getFieldValue(['lines', lineName, 'manual_product_code']);
                      const desc = form.getFieldValue(['lines', lineName, 'manual_product_description']);
                      if ((code ?? '').trim() || (desc ?? '').trim()) return;
                      throw new Error('Enter product code and/or description');
                    },
                  },
                ]}
              >
                <Input placeholder="Product or service description" maxLength={500} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 8]}>
            <Col xs={8} md={4}>
              <Form.Item
                label="Qty"
                name={[lineName, 'quantity']}
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber min={0.0001} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={8} md={4}>
              <Form.Item
                label="UOM"
                name={[lineName, 'uom']}
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input placeholder="pcs" />
              </Form.Item>
            </Col>
            <Col xs={8} md={6}>
              <Form.Item label="Stock type" name={[lineName, 'stock_type']} initialValue="MTS">
                <Select
                  options={[
                    { value: 'MTS', label: 'MTS (make-to-stock)' },
                    { value: 'MTO_hybrid', label: 'MTO / hybrid' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}
      <Row gutter={[16, 8]}>
        <Col xs={24} md={8}>
          <Form.Item label="Target production date" name={[lineName, 'target_production_date']}>
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label="BOM" name={[lineName, 'bom_id']}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={
                isManual
                  ? 'N/A for manual entry'
                  : itemId
                    ? bomOptions.length
                      ? 'Select BOM'
                      : 'No BOM for this item'
                    : 'Select item first'
              }
              options={bomOptions}
              disabled={isManual || !itemId}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label="Project (traceability)" name={[lineName, 'project_id']}>
            <Select allowClear showSearch optionFilterProp="label" placeholder="Optional" options={projectOptions} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[16, 8]}>
        <Col xs={24} md={8}>
          <Form.Item label="Line priority" name={[lineName, 'line_priority']}>
            <Select
              allowClear
              placeholder="Optional"
              options={['Low', 'Normal', 'High', 'Urgent'].map((p) => ({ value: p, label: p }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={16}>
          <Form.Item label="Line remarks" name={[lineName, 'line_remarks']}>
            <Input placeholder="Lot, customer ref, notes for this line" />
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );
}

export default function ProductionRequestFormPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [form] = Form.useForm();
  const poWaiverReason = Form.useWatch('po_waiver_reason', form);
  const linkedQuotationId = Form.useWatch('quotation_id', form);
  const isQuotationLinked = Boolean(linkedQuotationId);
  const [submitting, setSubmitting] = useState(false);
  const [quotationLinesLoading, setQuotationLinesLoading] = useState(false);
  const [supplementaryOf, setSupplementaryOf] = useState<{ id: string; request_number: string } | null>(null);

  const { data: warehouses = [], isLoading: whLoading } = useWarehouses();
  const { data: customers = [], isLoading: custLoading } = useActiveCustomers();
  const { data: items = [], isLoading: itemsLoading } = useItems({ is_active: true });
  const { data: projects = [], isLoading: projLoading } = useProjectsForSelection();
  const { data: boms = [], isLoading: bomsLoading } = useQuery({
    queryKey: ['boms', 'list-all'],
    queryFn: () => bomService.list(),
  });
  const { data: acceptedQuotations = [], isLoading: quotationsLoading } = useQuery({
    queryKey: ['quotations', 'accepted-latest'],
    queryFn: () => quotationService.getAll({ status: 'Accepted', is_latest_revision: true }),
  });
  const { data: quotationUsage, isLoading: usedQuotationsLoading } = useQuery({
    queryKey: ['production-requests', 'used-quotation-ids'],
    queryFn: () => prApi.getQuotationProductionRequestUsage(),
  });

  const projectOptions = useMemo(
    () =>
      projects.map((p: { id: string; project_number?: string; project_name: string }) => ({
        value: p.id,
        label: `${p.project_number ? `${p.project_number} — ` : ''}${p.project_name}`,
      })),
    [projects]
  );

  const requesterLabel = user
    ? [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username
    : '—';

  const onFinish = async (v: {
    request_date?: Dayjs;
    warehouse_id: string;
    department_id: string;
    customer_id?: string | null;
    quotation_id?: string | null;
    external_ref_no?: string | null;
    effective_date?: Dayjs | null;
    contact_person?: string | null;
    po_waiver_reason?: prApi.PoWaiverReason | null;
    po_waiver_reason_detail?: string | null;
    responsibility_notes?: string | null;
    priority?: string;
    source_type?: string;
    remarks?: string;
    lines: Array<{
      line_product_source?: 'inventory' | 'manual';
      item_id?: string;
      boq_line_item_id?: string | null;
      quotation_line_number?: number | string | null;
      manual_product_code?: string | null;
      manual_product_description?: string | null;
      quantity: number;
      uom: string;
      stock_type?: string;
      target_production_date?: Dayjs | null;
      bom_id?: string | null;
      project_id?: string | null;
      line_priority?: string | null;
      line_remarks?: string | null;
    }>;
  }) => {
    setSubmitting(true);
    try {
      const lines = (v.lines || []).map((line, i) => {
        const qty = line.quantity != null ? Number(line.quantity) : NaN;
        const uom = String(line.uom ?? '').trim();
        const isManual = (line.line_product_source ?? 'inventory') === 'manual';
        const quotationLineNumber =
          line.quotation_line_number != null && String(line.quotation_line_number).trim() !== ''
            ? Number(line.quotation_line_number)
            : null;
        if (isManual) {
          const mc = line.manual_product_code != null && String(line.manual_product_code).trim() !== '' ? String(line.manual_product_code).trim() : null;
          const md =
            line.manual_product_description != null && String(line.manual_product_description).trim() !== ''
              ? String(line.manual_product_description).trim()
              : null;
          return {
            line_number: i + 1,
            item_id: null,
            boq_line_item_id: nilUuid(line.boq_line_item_id),
            quotation_line_number: quotationLineNumber,
            manual_product_code: mc,
            manual_product_description: md,
            quantity: qty,
            uom,
            stock_type: (line.stock_type as 'MTS' | 'MTO_hybrid') || 'MTS',
            target_production_date: line.target_production_date
              ? dayjs(line.target_production_date).format('YYYY-MM-DD')
              : null,
            bom_id: null,
            project_id: nilUuid(line.project_id),
            line_priority: line.line_priority && String(line.line_priority).trim() !== '' ? line.line_priority : null,
            remarks: line.line_remarks != null && String(line.line_remarks).trim() !== '' ? String(line.line_remarks) : null,
          };
        }
        return {
          line_number: i + 1,
          item_id: line.item_id,
          boq_line_item_id: nilUuid(line.boq_line_item_id),
          quotation_line_number: quotationLineNumber,
          manual_product_code: null,
          manual_product_description: null,
          quantity: qty,
          uom,
          stock_type: (line.stock_type as 'MTS' | 'MTO_hybrid') || 'MTS',
          target_production_date: line.target_production_date
            ? dayjs(line.target_production_date).format('YYYY-MM-DD')
            : null,
          bom_id: nilUuid(line.bom_id),
          project_id: nilUuid(line.project_id),
          line_priority: line.line_priority && String(line.line_priority).trim() !== '' ? line.line_priority : null,
          remarks: line.line_remarks != null && String(line.line_remarks).trim() !== '' ? String(line.line_remarks) : null,
        };
      });

      const rawLines = v.lines || [];
      const badLine = lines.findIndex((l, idx) => {
        const raw = rawLines[idx];
        const isManual = (raw?.line_product_source ?? 'inventory') === 'manual';
        if (!Number.isFinite(l.quantity) || l.quantity <= 0 || !String(l.uom || '').trim()) return true;
        if (isManual) {
          const mc = (raw?.manual_product_code ?? '').trim();
          const md = (raw?.manual_product_description ?? '').trim();
          return !mc && !md;
        }
        return !raw?.item_id;
      });
      if (badLine >= 0) {
        message.error(`Line ${badLine + 1}: complete product details (inventory item or manual code/description), quantity, and UOM.`);
        return;
      }

      const reason = v.po_waiver_reason && String(v.po_waiver_reason).trim() !== '' ? v.po_waiver_reason : null;
      const created = await prApi.createProductionRequest({
        request_date: v.request_date ? dayjs(v.request_date).format('YYYY-MM-DD') : undefined,
        warehouse_id: v.warehouse_id,
        department_id: v.department_id,
        customer_id: nilUuid(v.customer_id),
        quotation_id: nilUuid(v.quotation_id),
        external_ref_no:
          v.external_ref_no != null && String(v.external_ref_no).trim() !== '' ? String(v.external_ref_no).trim() : null,
        effective_date: v.effective_date ? dayjs(v.effective_date).format('YYYY-MM-DD') : null,
        contact_person:
          v.contact_person != null && String(v.contact_person).trim() !== '' ? String(v.contact_person).trim() : null,
        po_waiver_reason: reason,
        po_waiver_reason_detail:
          reason === 'Other' && v.po_waiver_reason_detail != null && String(v.po_waiver_reason_detail).trim() !== ''
            ? String(v.po_waiver_reason_detail).trim()
            : null,
        responsibility_notes:
          v.responsibility_notes != null && String(v.responsibility_notes).trim() !== ''
            ? String(v.responsibility_notes).trim()
            : null,
        priority: v.priority || 'Normal',
        source_type: v.source_type || 'Manual',
        remarks: v.remarks != null && String(v.remarks).trim() !== '' ? String(v.remarks).trim() : null,
        lines,
      });
      message.success('Production request saved');
      queryClient.invalidateQueries({ queryKey: ['production-requests', 'used-quotation-ids'] });
      navigate(`/mfg/production-requests/${created.id}`);
    } catch (e: unknown) {
      message.error(formatPrCreateError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const loading =
    whLoading || custLoading || itemsLoading || projLoading || bomsLoading || quotationsLoading || usedQuotationsLoading;

  const usedQuotationIdSet = useMemo(() => new Set(quotationUsage?.quotation_ids ?? []), [quotationUsage]);
  const usageByQuotationId = useMemo(
    () => new Map((quotationUsage?.usage ?? []).map((u) => [u.quotation_id, u])),
    [quotationUsage]
  );

  const quotationOptions = useMemo(
    () =>
      acceptedQuotations
        .filter((q) => !usedQuotationIdSet.has(q.id))
        .map((q) => {
          const usage = usageByQuotationId.get(q.id);
          const base = `${q.quotation_number} — ${q.customer_name ?? q.quotation_name ?? ''}`.trim();
          return {
            value: q.id,
            label: usage?.has_remaining
              ? `${base} (supplementary to ${usage.parent_request_number ?? 'existing request'})`
              : base,
          };
        }),
    [acceptedQuotations, usedQuotationIdSet, usageByQuotationId]
  );

  const handleQuotationSelected = useCallback(
    async (quotationId: string | null) => {
      setSupplementaryOf(null);
      if (!quotationId) return;
      setQuotationLinesLoading(true);
      try {
        const {
          lines: built,
          skipped,
          customer_id,
          parent_production_request,
        } = await prApi.previewProductionRequestLinesFromQuotation(quotationId, 'remaining');
        setSupplementaryOf(parent_production_request);
        if (built.length === 0 && parent_production_request) {
          message.warning(
            `All quotation lines are already covered by ${parent_production_request.request_number} and related requests. Nothing remains to request.`
          );
          form.setFieldsValue({ lines: [] });
        } else if (built.length === 0) {
          message.warning(
            'No lines could be filled from the quotation text (code/description). Add lines manually.'
          );
          form.setFieldsValue({ lines: [{ uom: 'pcs', line_product_source: 'inventory' }] });
        } else {
          form.setFieldsValue({
            lines: built.map((row) => ({
              line_product_source: 'manual' as const,
              item_id: undefined,
              boq_line_item_id: row.boq_line_item_id,
              quotation_line_number: row.quotation_line_number,
              quote_quantity: row.quote_quantity,
              already_requested: row.already_requested,
              requested_in: row.requested_in,
              bom_id: undefined,
              manual_product_code: row.manual_product_code,
              manual_product_description: row.manual_product_description,
              quantity: row.quantity,
              uom: row.uom,
              stock_type: row.stock_type,
              project_id: row.project_id,
              line_remarks: row.line_remarks,
              target_production_date: null,
              line_priority: null,
            })),
          });
          if (skipped > 0) {
            message.warning(
              `Filled ${built.length} line(s) from the quotation. ${skipped} line(s) had no usable product text — add those manually.`
            );
          } else {
            message.success(`Filled ${built.length} line(s) from quotation (manual product text). You can switch a line to inventory if needed.`);
          }
        }
        if (customer_id) {
          form.setFieldsValue({ customer_id });
        }
        const q = acceptedQuotations.find((row) => row.id === quotationId);
        if (q?.department_id) {
          form.setFieldsValue({ department_id: q.department_id });
        }
      } catch (e: unknown) {
        message.error(formatPrCreateError(e));
      } finally {
        setQuotationLinesLoading(false);
      }
    },
    [form, message, acceptedQuotations]
  );

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <Button icon={<ArrowLeftOutlined />} type="link" onClick={() => navigate('/mfg/production-requests')} style={{ paddingLeft: 0 }}>
        Back to production requests
      </Button>

      <Title level={3} style={{ marginTop: 8 }}>
        New production request
      </Title>
      <Text type="secondary">
        Document number is assigned on save. The request is created in <strong>Draft</strong>; you can submit it for approval from the detail page.
      </Text>

      <Alert
        type="info"
        showIcon
        style={{ marginTop: 16, marginBottom: 16 }}
        message="Traceability"
        description={
          <span>
            Requested by is recorded as the signed-in user. Optionally set an expected customer when the build is based on an inquiry
            and the purchase order is not received yet. Use line-level project, target dates, BOM, and remarks to tie this request to projects and
            shop-floor planning.
          </span>
        }
      />

      {loading ? (
        <Spin style={{ marginTop: 48, display: 'block' }} />
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            request_date: dayjs(),
            priority: 'Normal',
            source_type: 'Manual',
            lines: [{ uom: 'pcs', line_product_source: 'inventory' }],
          }}
        >
          <Card title="Document" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item label="Request date" name="request_date">
                  <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Status (on save)">
                  <Input readOnly value="Draft" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Requested by">
                  <Input readOnly value={requesterLabel} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item name="warehouse_id" label="Warehouse" rules={[{ required: true, message: 'Select warehouse' }]}>
                  <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder="Receiving / issue warehouse"
                    options={warehouses.map((w: { id: string; warehouse_name: string }) => ({
                      value: w.id,
                      label: w.warehouse_name,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="department_id"
                  label="Department"
                  rules={[{ required: true, message: 'Department is required' }]}
                >
                  <DepartmentSelect />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="customer_id"
                  label="Customer (optional)"
                  tooltip="Use when production is for an expected or regular customer inquiry before the PO is received."
                >
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Select customer"
                    options={customers.map((c: { id: string; customer_code?: string; customer_name: string }) => ({
                      value: c.id,
                      label: `${c.customer_code ? `${c.customer_code} — ` : ''}${c.customer_name}`.trim(),
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="priority" label="Priority">
                  <Select options={['Low', 'Normal', 'High', 'Urgent'].map((p) => ({ value: p, label: p }))} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="source_type" label="Source type">
                  <Select
                    options={[
                      { value: 'Manual', label: 'Manual' },
                      { value: 'Reorder', label: 'Reorder level' },
                      { value: 'Forecast', label: 'Forecast' },
                      { value: 'MRP', label: 'MRP' },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="remarks" label="Header remarks">
              <Input.TextArea rows={3} placeholder="Business justification, campaign, or planning notes" />
            </Form.Item>
          </Card>

          <Card
            title="PO not yet received / approval to proceed"
            style={{ marginBottom: 16 }}
            extra={
              <Text type="secondary" style={{ fontSize: 12 }}>
                Optional; link an accepted quotation when you need production approval before the PO is on hand.
              </Text>
            }
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="quotation_id"
                  label="Quotation (accepted)"
                  tooltip="Latest accepted revision only. Quotations fully covered by existing production requests are hidden; a revised quotation with new or increased lines is offered as a supplementary request. Lines are prefilled from quotation/BOQ text as manual products (not tied to inventory). Adjust each line or switch to an inventory item. After save, upload the signed approval form PDF on the detail page before submitting for approval."
                >
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Search by quotation #"
                    options={quotationOptions}
                    loading={quotationLinesLoading}
                    onChange={(v) => void handleQuotationSelected(v)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="external_ref_no" label="External reference">
                  <Input placeholder="e.g. OPP-2026-0550" maxLength={100} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item name="effective_date" label="Effective date">
                  <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                </Form.Item>
              </Col>
              <Col xs={24} md={16}>
                <Form.Item name="contact_person" label="Customer contact">
                  <Input placeholder="Customer-side contact name" maxLength={255} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} md={10}>
                <Form.Item name="po_waiver_reason" label="Reason (PO waiver)">
                  <Select
                    allowClear
                    placeholder="Optional"
                    options={[
                      { value: 'PO_will_follow_before_delivery', label: 'PO will follow before delivery' },
                      { value: 'Other', label: 'Other' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={14}>
                {poWaiverReason === 'Other' ? (
                  <Form.Item
                    name="po_waiver_reason_detail"
                    label="Other — detail"
                    rules={[{ required: true, message: 'Enter detail when reason is Other' }]}
                  >
                    <Input.TextArea rows={2} placeholder="Describe the reason" />
                  </Form.Item>
                ) : null}
              </Col>
            </Row>
            <Form.Item name="responsibility_notes" label="Responsibility / scope notes">
              <Input.TextArea rows={3} placeholder="Notes under your responsibility, scope alignment, etc." />
            </Form.Item>
          </Card>

          <Card title="Lines">
            {supplementaryOf ? (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={`Supplementary request to ${supplementaryOf.request_number}`}
                description="This quotation (or an earlier revision of it) already has a production request. Only lines and quantities not yet requested are listed; the server rejects anything already covered."
              />
            ) : null}
            <Form.List name="lines">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <PrLineFields
                      key={field.key}
                      field={field}
                      lineIndex={index}
                      remove={remove}
                      items={items}
                      itemsLoading={itemsLoading}
                      boms={boms}
                      projectOptions={projectOptions}
                    />
                  ))}
                  <Tooltip
                    title={
                      isQuotationLinked
                        ? 'Lines are fixed to the selected quotation. Clear the quotation above to add lines manually.'
                        : undefined
                    }
                  >
                    <span style={{ display: 'block', width: '100%' }}>
                      <Button
                        type="dashed"
                        block
                        icon={<PlusOutlined />}
                        disabled={isQuotationLinked}
                        onClick={() => add({ uom: 'pcs', line_product_source: 'inventory' })}
                      >
                        Add line
                      </Button>
                    </span>
                  </Tooltip>
                </>
              )}
            </Form.List>
          </Card>

          <Space style={{ marginTop: 24 }}>
            <Button onClick={() => navigate('/mfg/production-requests')}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Save draft
            </Button>
          </Space>
        </Form>
      )}
    </div>
  );
}
